import { type ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Home, Calendar, Bell, Banknote, User, ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  apiGetNotificationUnreadCount,
  apiGetCompanyLogo,
} from "@/lib/api";
import { ensurePushSubscription } from "@/lib/push";
import { PullToRefresh } from "@/components/mobile/PullToRefresh";
import { haptic } from "@/lib/haptics";
import { cn } from "@/lib/utils";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { getMobileNavigation } from "@/lib/mobile-navigation";
import { MobileProductTour } from "@/components/tour/MobileProductTour";

function DesktopRedirect() {
  const [isDesktop] = useState(() => window.innerWidth >= 768);
  if (isDesktop) return <Navigate to="/" replace />;
  return null;
}

const NAV_ICONS = { home: Home, calendar: Calendar, inbox: Bell, salary: Banknote, profile: User };

export function BottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const { canAccess } = useModuleAccess();

  const { data: unreadNotifs } = useQuery({
    queryKey: ["mobile-notif-count"],
    queryFn: apiGetNotificationUnreadCount,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const inboxBadge = unreadNotifs?.count ?? 0;

  // App icon badge (Badging API) — reflects unread inbox count on the
  // home-screen icon. Supported on Android PWAs and desktop; no-ops elsewhere.
  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    try {
      if ("setAppBadge" in nav) {
        if (inboxBadge > 0) nav.setAppBadge?.(inboxBadge).catch(() => {});
        else nav.clearAppBadge?.().catch(() => {});
      }
    } catch {
      /* ignore */
    }
  }, [inboxBadge]);

  return (
    <nav
      className="shrink-0 flex items-center justify-around border-t border-border/40 bg-background"
      style={{ paddingBottom: "14px", paddingTop: "8px" }}
    >
      {getMobileNavigation(canAccess).map(({ to, key, exact }) => {
        const Icon = NAV_ICONS[key];
        const active = exact
          ? location.pathname === to
          : location.pathname.startsWith(to.split("?")[0]);
        const label = t(`mobile.nav.${key}`);
        return (
          <Link
            key={to}
            to={to}
            onClick={() => haptic()}
            data-tour-m={`nav-${key}`}
            className={cn(
              "flex flex-col items-center gap-1 flex-1 py-2 transition-colors",
              active ? "text-primary" : "text-muted-foreground"
            )}
          >
            <div className="relative">
              <Icon className="h-6 w-6" strokeWidth={active ? 2.5 : 1.75} />
              {key === "inbox" && inboxBadge > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-2 -right-2.5 h-4 min-w-4 px-1 text-[10px] leading-none flex items-center justify-center"
                >
                  {inboxBadge > 99 ? "99+" : inboxBadge}
                </Badge>
              )}
            </div>
            <span className={cn("text-[10px] leading-none", active ? "font-semibold" : "font-normal")}>{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * When the page is restored from iOS Safari's back-forward cache (after
 * returning from an external OAuth redirect), the layout viewport ends up
 * stale and the bottom nav floats above a blank strip. CSS-level fixes
 * are unreliable across iOS versions, so we take the user's suggestion:
 * just force a full reload. That guarantees a fresh paint with the
 * current viewport, no matter what state the bfcache left things in.
 */
function useBfcacheReload() {
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
}

interface MobileLayoutProps {
  children: ReactNode;
  header?: ReactNode;
  title?: string;
  /** Show a back button on the left of the header (calls `navigate(-1)`). */
  showBack?: boolean;
  /** Hide the org logo on the right (e.g. the Profile header has its own right-side controls). */
  hideOrgLogo?: boolean;
}

export default function MobileLayout({ children, header, title, showBack, hideOrgLogo }: MobileLayoutProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  useBfcacheReload();

  // Organization logo shown on the right of the header (any authenticated user).
  const { data: orgLogoUrl } = useQuery({
    queryKey: ["company-logo"],
    queryFn: apiGetCompanyLogo,
    staleTime: 60 * 60_000,
  });

  // Web Push: try to subscribe on mount, and again on the first tap — iOS
  // requires a user gesture for the permission prompt. No-ops once subscribed.
  useEffect(() => {
    ensurePushSubscription();
    const onFirstTap = () => ensurePushSubscription();
    window.addEventListener("pointerdown", onFirstTap, { once: true });
    return () => window.removeEventListener("pointerdown", onFirstTap);
  }, []);

  // Foreground refresh: when the app returns from the background / lock screen,
  // refetch everything so the user never stares at stale data (the global
  // refetchOnWindowFocus is off for desktop, so we do it explicitly here).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") queryClient.invalidateQueries();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [queryClient]);

  return (
    <>
      <DesktopRedirect />
      {/* `height: 100dvh` (dynamic viewport height) always matches the
          *currently visible* area — it adjusts when iOS Safari's URL bar
          shows or hides. So the flex container is exactly screen-sized,
          and the nav at the end of the column sits flush at the visible
          bottom regardless of Safari chrome state. */}
      <div
        className="fixed top-0 left-0 right-0 flex flex-col bg-background"
        style={{ height: "100dvh" }}
      >
        {/* Top bar */}
        <header className="relative flex items-center px-4 h-14 border-b border-border/40 shrink-0 bg-background/95 backdrop-blur">
          {header ?? (
            <>
              {showBack ? (
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  aria-label={t("common.back")}
                  className="-ml-2 p-2 -mr-1 text-foreground/80 hover:text-foreground active:bg-muted/60 rounded-md transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              ) : (
                <Link to="/mobile" aria-label={t("mobile.nav.home")}>
                  <img src="/besthr.png" alt="BestHR" className="h-7 object-contain" />
                </Link>
              )}
              {title && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-base font-bold text-foreground">{title}</span>
                </div>
              )}
            </>
          )}

          {/* Organization logo — right side, on top of any header content */}
          {orgLogoUrl && !hideOrgLogo && (
            <img
              src={orgLogoUrl}
              alt="Company logo"
              className="absolute right-4 top-1/2 -translate-y-1/2 h-7 max-w-[96px] object-contain pointer-events-none"
            />
          )}
        </header>

        {/* Scrollable content with pull-to-refresh */}
        <PullToRefresh
          onRefresh={() => queryClient.invalidateQueries()}
          className="flex-1 min-h-0"
        >
          {children}
        </PullToRefresh>

        {/* Bottom navigation */}
        <BottomNav />
      </div>
      <MobileProductTour />
    </>
  );
}
